import React, { useEffect, useState } from 'react';

const resolveDisplayName = ({ linkedProfile, linkedRequest, agreement, authUser }) => {
  return (
    linkedProfile?.fullName ||
    linkedRequest?.fullName ||
    agreement?.formData?.fullName ||
    authUser?.displayName ||
    authUser?.username ||
    (authUser?.email ? authUser.email.split('@')[0] : 'Franchisee')
  );
};

const resolvePhoneNumber = ({ linkedProfile, linkedRequest, agreement, authUser }) => {
  return (
    linkedProfile?.phoneNumber ||
    linkedRequest?.phone ||
    agreement?.formData?.phone ||
    agreement?.formData?.mobile ||
    authUser?.phoneNumber ||
    null
  );
};

const ChannelPartnerForm = ({ firebase, project, onSuccess, onError }) => {
  const [submitting, setSubmitting] = useState(false);
  const [inlineMessage, setInlineMessage] = useState('');
  const [registered, setRegistered] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [checkingRegistration, setCheckingRegistration] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const getStorageKey = (uid) => `exclusive_mandate_registered:${project.id}:${uid}`;

  useEffect(() => {
    let active = true;

    const checkRegistration = async () => {
      if (!firebase?.db?.collection) {
        if (active) {
          setCheckingRegistration(false);
        }
        return;
      }

      try {
        const user = firebase?.auth?.currentUser || null;
        if (!user?.uid) {
          if (active) {
            setCheckingRegistration(false);
          }
          return;
        }

        const storageKey = getStorageKey(user.uid);
        const cached = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
        if (cached === 'true') {
          if (active) {
            setRegistered(true);
            setConfirmed(true);
            setCheckingRegistration(false);
          }
          return;
        }

        const snapshot = await firebase.db
          .collection('exclusive_mandate_registrations')
          .where('registeredByUid', '==', user.uid)
          .where('projectId', '==', project.id)
          .limit(1)
          .get();

        if (active) {
          if (snapshot && !snapshot.empty) {
            const doc = snapshot.docs[0];
            const data = doc.data();
            const isConfirmed = Boolean(data?.confirmedAt || data?.status === 'confirmed');
            setRegistered(true);
            setConfirmed(isConfirmed);
            if (isConfirmed) {
              window.localStorage.setItem(storageKey, 'true');
            }
          } else {
            setRegistered(false);
            setConfirmed(false);
          }
        }
      } catch (error) {
        console.error('Failed to check registration status:', error);
      } finally {
        if (active) {
          setCheckingRegistration(false);
        }
      }
    };

    checkRegistration();

    return () => {
      active = false;
    };
  }, [firebase, project.id]);

  const handleSubmit = async () => {
    setInlineMessage('');

    setSubmitting(true);
    try {
      const user = firebase?.auth?.currentUser || null;
      let linkedProfile = null;
      let linkedRequest = null;
      let agreement = null;

      if (firebase?.db?.collection && user?.uid) {
        const linkedRequestPromise = user?.email
          ? firebase.db
              .collection('onboardingRequests')
              .where('assignedEmail', '==', user.email)
              .limit(1)
              .get()
          : Promise.resolve(null);

        const linkedRequestSnap = await linkedRequestPromise;
        if (linkedRequestSnap && !linkedRequestSnap.empty) {
          linkedRequest = { id: linkedRequestSnap.docs[0].id, ...linkedRequestSnap.docs[0].data() };
        }

        const effectiveUserId = linkedRequest?.userId || linkedRequest?.id || user.uid;

        const [linkedProfileSnap, agreementSnap] = await Promise.all([
          firebase.db.collection('users').doc(effectiveUserId).get(),
          firebase.db
            .collection('franchiseAgreements')
            .where('userId', '==', effectiveUserId)
            .limit(1)
            .get(),
        ]);

        if (linkedProfileSnap?.exists) {
          linkedProfile = { id: linkedProfileSnap.id, ...linkedProfileSnap.data() };
        }

        if (agreementSnap && !agreementSnap.empty) {
          agreement = { id: agreementSnap.docs[0].id, ...agreementSnap.docs[0].data() };
        }
      }

      const resolvedName = resolveDisplayName({
        linkedProfile,
        linkedRequest,
        agreement,
        authUser: user,
      });
      const resolvedPhone = resolvePhoneNumber({
        linkedProfile,
        linkedRequest,
        agreement,
        authUser: user,
      });

      const payload = {
        projectId: project.id,
        projectName: project.name,
        name: resolvedName || null,
        email: user?.email || null,
        phone: resolvedPhone || null,
        company: null,
        city: null,
        requirements: null,
        consent: true,
        source: 'exclusive_mandates',
        registeredByUid: user?.uid || null,
        status: 'pending',
        confirmedAt: null,
        confirmedBy: null,
        projectSnapshot: {
          id: project.id,
          name: project.name,
          subtitle: project.subtitle || null,
          location: project.location || null,
        },
        createdAt: firebase?.fieldValue?.serverTimestamp
          ? firebase.fieldValue.serverTimestamp()
          : new Date(),
      };

      if (firebase?.db?.collection) {
        await firebase.db.collection('exclusive_mandate_registrations').add(payload);
      }

      setInlineMessage('You are registered as a channel partner. We will reach out shortly.');
      setRegistered(true);
      setConfirmed(false);
      if (user?.uid && typeof window !== 'undefined') {
        window.localStorage.removeItem(getStorageKey(user.uid));
      }
      onSuccess?.('Registered successfully.');
    } catch (err) {
      console.error('Failed to submit lead:', err);
      setInlineMessage('Submission failed. Please try again.');
      setRegistered(false);
      onError?.('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenConfirm = () => {
    if (registered || submitting || checkingRegistration) return;
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    handleSubmit();
  };

  return (
    <div className="exclusive-form">
      <h3>Register as Channel Partner</h3>
      <p>
        Exclusive mandate for Douglas &amp; McCartney franchisees. Register as a channel partner
        for priority access and updates.
      </p>

      {inlineMessage ? <div className="exclusive-form-message">{inlineMessage}</div> : null}

      {registered && !confirmed ? (
        <div className="exclusive-form-note">
          * We’ll contact you shortly with the next steps.
        </div>
      ) : null}

      <button
        type="button"
        className="exclusive-submit"
        onClick={handleOpenConfirm}
        disabled={submitting || registered || checkingRegistration}
      >
        {confirmed
          ? 'YOU ARE REGISTERED AS A CP'
          : registered
            ? 'WAITING FOR CONFIRMATION'
          : checkingRegistration
            ? 'Checking status...'
            : submitting
              ? 'Submitting...'
              : 'REGISTER ME AS THE CP'}
      </button>

      {confirmOpen ? (
        <div className="exclusive-confirm-overlay" onClick={() => setConfirmOpen(false)}>
          <div
            className="exclusive-confirm-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="exclusive-confirm-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 3h10l2 2v14l-2 2H7l-2-2V5l2-2z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M9 12l2 2 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h4>Confirm Registration</h4>
            <p>Do you want to register as a channel partner for this mandate?</p>
            <div className="exclusive-confirm-actions">
              <button type="button" onClick={handleConfirm}>
                Confirm
              </button>
              <button type="button" onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ChannelPartnerForm;
